/* BankInstructionsCard — Mercury-style tabbed funding instructions.
 *
 * Renders one payment_methods row as a tabbed card:
 *    ACH · Domestic Wire · International Wire
 *
 * Non-sensitive fields (bank name/address, beneficiary name/address, SWIFT,
 * currency) are shown clear with per-field copy buttons. Sensitive numbers
 * (routing, account, IBAN, intermediary account) use the existing
 * RevealableNumber component — password-gated, 30s auto-hide.
 *
 * Footer:
 *    [Copy all <rail> instructions]   [Download PDF]
 *
 * The PDF endpoint (GET /api/payment-methods/{id}/instructions.pdf) masks
 * sensitive numbers by default. Adding ?reveal=1 unlocks the full PDF, but
 * the server requires a successful password-reveal within the last 120s
 * (audit-logged) — same security model as the on-screen reveal.
 *
 * Props:
 *    method     — full payment_methods row from GET /payment-methods
 *    testIdRoot — used for data-testid composition
 */
import { useMemo, useState } from "react";
import {
  Building2, Copy, CheckCircle2, Clock, Download, Lock, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import RevealableNumber from "./RevealableNumber";

/* ── Helpers ──────────────────────────────────────────────────────── */
function addrLines(prefix, m) {
  const a1 = m[`${prefix}_address_line1`] || "";
  const a2 = m[`${prefix}_address_line2`] || "";
  const city = m[`${prefix}_city`] || "";
  const state = m[`${prefix}_state`] || "";
  const zip   = m[`${prefix}_postal_code`] || "";
  const country = m[`${prefix}_country`] || "";
  const cityLine = [city, state, zip].filter(Boolean).join(", ");
  return [a1, a2, cityLine, country].filter(Boolean).join("\n");
}

async function copyText(text, label = "Copied") {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
    // PCI hygiene — clear clipboard after 60s so a leftover Cmd-V doesn't
    // leak the number into the next app the user pastes into.
    setTimeout(() => {
      navigator.clipboard.writeText("").catch(() => {});
    }, 60_000);
  } catch {
    toast.error("Could not copy. Try Ctrl/Cmd+C manually.");
  }
}

function railsOf(m) {
  return (m?.rails || "").split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
}

/* ── Single field row — non-sensitive, always clear ────────────────── */
function Field({ label, value, multiline, mono, testId }) {
  const has = !!value;
  return (
    <div className="rounded-xl border border-border bg-secondary/30 px-3 py-2" data-testid={testId}>
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="flex items-start gap-2 mt-1">
        <div className={`flex-1 min-w-0 text-[13px] ${mono ? "mono" : ""} ${multiline ? "whitespace-pre-line" : "truncate"} ${has ? "" : "text-foreground/40"}`}>
          {has ? value : "—"}
        </div>
        {has && (
          <button
            type="button"
            onClick={() => copyText(value, `${label} copied`)}
            className="text-foreground/45 hover:text-foreground transition-colors shrink-0"
            title="Copy"
            data-testid={testId ? `${testId}-copy` : undefined}
          >
            <Copy className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
    </div>
  );
}

/* ── ACH panel ────────────────────────────────────────────────────── */
function AchPanel({ m, testIdRoot }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Beneficiary name"    value={m.account_holder} testId={`${testIdRoot}-ach-beneficiary-name`}/>
        <Field label="Beneficiary address" value={addrLines("account_holder", m)} multiline testId={`${testIdRoot}-ach-beneficiary-address`}/>
        <Field label="Bank name"           value={m.bank_name} testId={`${testIdRoot}-ach-bank-name`}/>
        <Field label="Bank address"        value={addrLines("bank", m)} multiline testId={`${testIdRoot}-ach-bank-address`}/>
        <RevealableNumber
          methodId={m.id} field="routing_number"
          last4={m.last4} digitsHint={9}
          label="ACH routing #"
          testIdPrefix={`${testIdRoot}-ach`}
        />
        <RevealableNumber
          methodId={m.id} field="account_number"
          last4={m.last4} digitsHint={12}
          label="Account #"
          testIdPrefix={`${testIdRoot}-ach`}
        />
        {m.account_type && <Field label="Account type" value={m.account_type.charAt(0).toUpperCase() + m.account_type.slice(1)}/>}
        <Field label="Currency"            value={m.currency || "USD"} mono/>
      </div>
      <CopyAllButton kind="ach" m={m} testIdRoot={testIdRoot}/>
    </div>
  );
}

/* ── Wire panel ───────────────────────────────────────────────────── */
function WirePanel({ m, testIdRoot }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Beneficiary name"    value={m.account_holder} testId={`${testIdRoot}-wire-beneficiary-name`}/>
        <Field label="Beneficiary address" value={addrLines("account_holder", m)} multiline testId={`${testIdRoot}-wire-beneficiary-address`}/>
        <Field label="Bank name"           value={m.bank_name} testId={`${testIdRoot}-wire-bank-name`}/>
        <Field label="Bank address"        value={addrLines("bank", m)} multiline testId={`${testIdRoot}-wire-bank-address`}/>
        <RevealableNumber
          methodId={m.id} field="wire_routing_number"
          last4={m.last4} digitsHint={9}
          label="Wire routing # (ABA)"
          testIdPrefix={`${testIdRoot}-wire`}
        />
        <RevealableNumber
          methodId={m.id} field="account_number"
          last4={m.last4} digitsHint={12}
          label="Account #"
          testIdPrefix={`${testIdRoot}-wire`}
        />
        {m.swift_bic && <Field label="SWIFT/BIC" value={m.swift_bic} mono testId={`${testIdRoot}-wire-swift`}/>}
        <Field label="Currency"            value={m.currency || "USD"} mono/>
      </div>
      <CopyAllButton kind="wire" m={m} testIdRoot={testIdRoot}/>
    </div>
  );
}

/* ── International wire panel ─────────────────────────────────────── */
function IntlPanel({ m, testIdRoot }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Beneficiary name"    value={m.account_holder} testId={`${testIdRoot}-intl-beneficiary-name`}/>
        <Field label="Beneficiary address" value={addrLines("account_holder", m)} multiline testId={`${testIdRoot}-intl-beneficiary-address`}/>
        <Field label="Bank name"           value={m.bank_name} testId={`${testIdRoot}-intl-bank-name`}/>
        <Field label="Bank address"        value={addrLines("bank", m)} multiline testId={`${testIdRoot}-intl-bank-address`}/>
        <Field label="SWIFT/BIC"           value={m.swift_bic} mono testId={`${testIdRoot}-intl-swift`}/>
        <RevealableNumber
          methodId={m.id} field="iban"
          last4={m.last4} digitsHint={24}
          label="IBAN"
          testIdPrefix={`${testIdRoot}-intl`}
        />
        {m.intermediary_bank_name && (
          <>
            <Field label="Intermediary bank" value={m.intermediary_bank_name}/>
            {m.intermediary_swift_bic && <Field label="Intermediary SWIFT/BIC" value={m.intermediary_swift_bic} mono/>}
            <RevealableNumber
              methodId={m.id} field="intermediary_account_number"
              last4={m.last4} digitsHint={12}
              label="Intermediary account #"
              testIdPrefix={`${testIdRoot}-intl`}
            />
          </>
        )}
        {m.purpose_of_payment && <Field label="Purpose of payment" value={m.purpose_of_payment} testId={`${testIdRoot}-intl-purpose`}/>}
      </div>
      <CopyAllButton kind="intl" m={m} testIdRoot={testIdRoot}/>
    </div>
  );
}

/* ── Copy-all-block button — masks the sensitive numbers ──────────── */
function CopyAllButton({ kind, m, testIdRoot }) {
  const blockText = useMemo(() => {
    const lines = [];
    if (kind === "ach")  lines.push("ACH Transfer Instructions");
    if (kind === "wire") lines.push("Domestic Wire Instructions");
    if (kind === "intl") lines.push("International Wire Instructions");
    lines.push("");
    if (m.account_holder)         lines.push(`Beneficiary name:    ${m.account_holder}`);
    const bAddr = addrLines("account_holder", m);
    if (bAddr)                    lines.push(`Beneficiary address: ${bAddr.replace(/\n/g, ", ")}`);
    if (m.bank_name)              lines.push(`Bank name:           ${m.bank_name}`);
    const aAddr = addrLines("bank", m);
    if (aAddr)                    lines.push(`Bank address:        ${aAddr.replace(/\n/g, ", ")}`);
    if (kind === "ach")           lines.push(`ACH routing #:       •••••${m.last4 ? "" : ""} — reveal in app to copy`);
    if (kind === "wire")          lines.push(`Wire routing # (ABA): •••• — reveal in app to copy`);
    if (kind === "intl" && m.swift_bic) lines.push(`SWIFT/BIC:           ${m.swift_bic}`);
    if (kind !== "intl")          lines.push(`Account #:           ••••${m.last4 || ""} — reveal in app to copy`);
    if (m.currency)               lines.push(`Currency:            ${m.currency}`);
    lines.push("");
    lines.push("Sensitive numbers masked. Re-authenticate in the verqo wallet to reveal.");
    return lines.join("\n");
  }, [kind, m]);

  return (
    <button
      type="button"
      onClick={() => copyText(blockText, `${kind === "ach" ? "ACH" : kind === "wire" ? "Wire" : "International wire"} instructions copied`)}
      data-testid={`${testIdRoot}-${kind}-copy-all`}
      className="mt-3 inline-flex items-center gap-2 rounded-full bg-foreground/[0.05] hover:bg-foreground/10 border border-foreground/15 px-3.5 h-9 text-[12.5px]"
    >
      <Copy className="w-3.5 h-3.5"/>Copy all {kind === "ach" ? "ACH" : kind === "wire" ? "Wire" : "International"} instructions (masked)
    </button>
  );
}

/* ── Download PDF button — kicks the auth cookie + downloads ──────── */
function DownloadPdfBtn({ methodId, testIdRoot }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const r = await api.get(
        `/payment-methods/${methodId}/instructions.pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wire-ach-instructions-${methodId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded — sensitive numbers masked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download PDF");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`${testIdRoot}-download-pdf`}
      className="inline-flex items-center gap-2 rounded-full bg-foreground/[0.05] hover:bg-foreground/10 border border-foreground/15 px-3.5 h-9 text-[12.5px] disabled:opacity-50"
    >
      <Download className="w-3.5 h-3.5"/>{busy ? "Preparing…" : "Download PDF"}
    </button>
  );
}

/* ── Main component ───────────────────────────────────────────────── */
export default function BankInstructionsCard({ method, testIdRoot = "bank-instr" }) {
  const rails = railsOf(method);
  // Default to the first configured rail (ACH > wire > intl).
  const defaultTab = rails.includes("ach")
    ? "ach"
    : rails.includes("wire")
      ? "wire"
      : rails.includes("intl_wire") || method.iban
        ? "intl"
        : "ach";
  const [tab, setTab] = useState(defaultTab);

  // Available tabs depend on which rails are configured on this method.
  const TABS = useMemo(() => {
    const t = [];
    if (rails.includes("ach")) t.push({ k: "ach", l: "ACH", sub: "0–3 business days · no fee" });
    if (rails.includes("wire")) t.push({ k: "wire", l: "Domestic Wire", sub: "Same-day before 5pm ET" });
    if (rails.includes("intl_wire") || method.iban) t.push({ k: "intl", l: "International", sub: "1–3 business days · SWIFT" });
    return t;
  }, [rails, method.iban]);

  if (TABS.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-foreground/20 bg-secondary/30 p-5 text-center">
        <div className="text-[12px] text-foreground/60">
          No transfer rails configured on this bank yet. Add ACH, wire, or SWIFT details to render instructions.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white" data-testid={`${testIdRoot}-card`}>
      {/* Bank header */}
      <div className="flex items-start gap-3 pb-4 border-b border-border">
        <div className="w-10 h-10 rounded-xl bg-secondary grid place-items-center shrink-0">
          <Building2 className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[14px] truncate">{method.bank_name || method.label || "Bank account"}</div>
          <div className="text-[11px] text-foreground/55 mono mt-0.5">
            ···· {method.last4 || "----"} · {method.currency || "USD"} · {TABS.map(t => t.l).join(" · ")}
          </div>
        </div>
        {method.verified && (
          <span className="chip bg-emerald-50 border-emerald-200 text-emerald-800 text-[10px] inline-flex items-center gap-1">
            <CheckCircle2 className="w-2.5 h-2.5"/>Verified
          </span>
        )}
      </div>

      {/* Tab strip */}
      <div className="border-b border-border">
        <div className="flex gap-1 overflow-x-auto" data-testid={`${testIdRoot}-tabs`}>
          {TABS.map(t => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`${testIdRoot}-tab-${t.k}`}
              className={`pb-3 pt-3 px-4 text-sm border-b-2 transition-colors shrink-0 ${
                tab === t.k
                  ? "border-foreground text-foreground"
                  : "border-transparent text-foreground/55 hover:text-foreground"
              }`}
            >
              <div className="font-medium">{t.l}</div>
              <div className="text-[10px] text-foreground/45 mono mt-0.5">{t.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Active panel */}
      <div className="pt-4">
        {tab === "ach"  && <AchPanel  m={method} testIdRoot={testIdRoot}/>}
        {tab === "wire" && <WirePanel m={method} testIdRoot={testIdRoot}/>}
        {tab === "intl" && <IntlPanel m={method} testIdRoot={testIdRoot}/>}
      </div>

      {/* Footer */}
      <div className="mt-5 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-3">
        <div className="text-[11px] text-foreground/55 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5"/>
          <span>
            ACH 0–3 business days
            {rails.includes("wire") && " · Domestic wire same-day before 5pm ET"}
            {(rails.includes("intl_wire") || method.iban) && " · International 1–3 business days"}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DownloadPdfBtn methodId={method.id} testIdRoot={testIdRoot}/>
        </div>
      </div>
      <div className="mt-2 text-[10.5px] text-foreground/45 inline-flex items-center gap-1.5">
        <Lock className="w-2.5 h-2.5"/>
        Sensitive numbers are masked by default. Click Show + re-enter your password to reveal.
      </div>
    </div>
  );
}
